import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { useNotification } from '../../../context/NotificationContext';
import ContactTable from './ContactTable';
import ContactDetail from './ContactDetail';
import Loading from '../../common/Loading';
import Button from '../../common/Button';
import { FiRefreshCw, FiFilter } from 'react-icons/fi';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    limit: 10
  });
  const [totalPages, setTotalPages] = useState(1);
  const [showDetail, setShowDetail] = useState(false);

  const { request, loading } = useApi();
  const { showNotification } = useNotification();

  const statusOptions = [
    { value: 'all', label: 'Semua Status' },
    { value: 'new', label: 'Baru' },
    { value: 'read', label: 'Sudah Dibaca' },
    { value: 'replied', label: 'Sudah Dibalas' },
    { value: 'closed', label: 'Ditutup' }
  ];

  useEffect(() => {
    fetchContacts();
  }, [filters]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await request(`/contacts?${params.toString()}`);
      setContacts(response.data || []);
      setTotalPages(Math.ceil((response.total || 0) / filters.limit));
    } catch (error) {
      showNotification('Error mengambil data kontak', 'error');
    }
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      await request(`/contacts/${contactId}/status`, {
        method: 'PUT',
        data: { status: newStatus }
      });
      
      setContacts(prev => prev.map(contact => 
        contact.id === contactId ? { ...contact, status: newStatus } : contact
      ));
      
      showNotification('Status berhasil diupdate', 'success');
    } catch (error) {
      showNotification('Error mengupdate status', 'error');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Yakin ingin menghapus pesan ini?')) return;

    try {
      await request(`/contacts/${contactId}`, { method: 'DELETE' });
      setContacts(prev => prev.filter(contact => contact.id !== contactId));
      showNotification('Pesan berhasil dihapus', 'success');
    } catch (error) {
      showNotification('Error menghapus pesan', 'error');
    }
  };

  const handleViewDetail = (contact) => {
    setSelectedContact(contact);
    setShowDetail(true);
    
    // Mark as read if new
    if (contact.status === 'new') {
      handleStatusUpdate(contact.id, 'read');
    }
  };

  if (loading && contacts.length === 0) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manajemen Kontak</h1>
          <p className="text-gray-600">Kelola pesan dan inquiry dari pengunjung</p>
        </div>
        <Button
          onClick={fetchContacts}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center gap-4">
          <FiFilter className="text-gray-500" />
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Contact Table */}
      <ContactTable
        contacts={contacts}
        onViewDetail={handleViewDetail}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
        loading={loading}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button
            variant="outline"
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {filters.page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={filters.page === totalPages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Contact Detail Modal */}
      {showDetail && selectedContact && (
        <ContactDetail
          contact={selectedContact}
          onClose={() => {
            setShowDetail(false);
            setSelectedContact(null);
          }}
          onStatusUpdate={handleStatusUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ContactManagement;